import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { z } from "zod";

import { Badge } from "~/components/ui/badge";
import { ButtonRouter } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { PageHeader } from "~/components/ui/page-title";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { pageEditorQueryOptions } from "~/lib/functions/organization-pages";
import { SUPPORT_EMAIL } from "~/lib/shared/brand";
import type {
  PageEditState,
  ProgramPageSummary,
} from "~/lib/shared/organization-pages";

import { PageEditor } from "./-page-editor";

export const Route = createFileRoute("/_user/my-organization/$slug/pages/")({
  staticData: { title: "Our pages" },
  validateSearch: z.object({
    tab: z.enum(["organization", "programs"]).optional().catch(undefined),
  }),
  loader: async ({ context, params }) => {
    await context.queryClient.ensureQueryData(
      pageEditorQueryOptions({ slug: params.slug }),
    );
    return { title: "Our pages" };
  },
  component: OurPagesPage,
});

const STATE_LABEL: Record<Exclude<PageEditState, "live">, string> = {
  draft: "Draft",
  in_review: "Changes waiting for review",
};

function ProgramRow({
  slug,
  program,
}: {
  slug: string;
  program: ProgramPageSummary;
}): React.JSX.Element {
  return (
    <div className="flex items-center justify-between gap-4 px-4 py-3">
      <div className="min-w-0 space-y-1">
        <div className="text-base font-medium text-gray-900">
          {program.name}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {program.state === "live" ? (
            <span className="text-text-tertiary text-sm">
              Published
              {program.accreditationLevel
                ? ` · ${program.accreditationLevel}`
                : ""}
            </span>
          ) : program.state === "in_review" ? (
            <Badge variant="pending" size="sm">
              {STATE_LABEL.in_review}
            </Badge>
          ) : (
            <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-1 text-xs leading-none text-amber-800">
              {STATE_LABEL.draft}
            </span>
          )}
        </div>
      </div>
      <ButtonRouter
        to="/my-organization/$slug/pages/$programId"
        params={{ slug, programId: program.id }}
        variant="default"
        size="sm"
      >
        {program.state === "live" ? "Edit" : "Open"}
      </ButtonRouter>
    </div>
  );
}

function OurPagesPage(): React.JSX.Element {
  const { slug } = Route.useParams();
  const { tab = "organization" } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { data } = useQuery(pageEditorQueryOptions({ slug }));
  if (!data) return <></>;

  const missingProgram = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(`A program of ${data.organization.name} is missing`)}`;

  return (
    <div className="max-w-4xl space-y-8">
      <PageHeader
        eyebrow={data.organization.name}
        title="Our pages"
        description="Keep your organization and program pages on Dream Coach Match current. Your edits are a draft until DCM reviews and publishes them."
      />
      <Tabs
        value={tab}
        onValueChange={(value) =>
          void navigate({
            search: {
              tab: value === "programs" ? "programs" : undefined,
            },
            replace: true,
          })
        }
      >
        <TabsList>
          <TabsTrigger value="organization">Organization</TabsTrigger>
          <TabsTrigger value="programs">
            Programs · {data.programs.length}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="organization" className="mt-6">
          <PageEditor data={data} target={{ slug }} />
        </TabsContent>
        <TabsContent value="programs" className="mt-6 space-y-4">
          <p className="text-text-secondary text-sm">
            Programs {data.organization.name} runs on Dream Coach Match. Each
            opens the same kind of editor. Name, accreditation level, ICF hours
            and accrediting bodies are set by DCM.
          </p>
          {data.programs.length > 0 ? (
            <Card className="gap-0 divide-y divide-gray-100 p-0">
              {data.programs.map((program) => (
                <ProgramRow key={program.id} slug={slug} program={program} />
              ))}
            </Card>
          ) : (
            <p className="text-text-tertiary text-sm">
              No programs of {data.organization.name} are on Dream Coach Match
              yet.
            </p>
          )}
          <p className="text-text-tertiary text-sm">
            A program missing?{" "}
            <a
              href={missingProgram}
              className="text-purple-700 underline underline-offset-2"
            >
              Tell DCM
            </a>{" "}
            and we'll add it.{" "}
            <Link
              to="/my-organization/$slug"
              params={{ slug }}
              className="text-purple-700 underline underline-offset-2"
            >
              Back to {data.organization.name}
            </Link>
          </p>
        </TabsContent>
      </Tabs>
    </div>
  );
}
